/**
 * 📡 گزارش خطای پنل ادمین به مرکز پایش.
 *
 * قرینهٔ `figano-web/src/lib/errorReporter.ts` است و همان قواعد را دارد:
 * هرگز مانع کاربر نمی‌شود، دسته‌ای می‌فرستد، نویز را فیلتر می‌کند و سقف دارد.
 *
 * تفاوت مهم با نسخهٔ وب: اینجا کاربر همیشه لاگین است، پس توکن را همراه
 * می‌فرستیم تا خطا به کارشناسِ مشخصی بچسبد. وقتی یک ادمین می‌گوید «صفحه‌ام
 * کار نمی‌کند»، همین که بدانی خطا برای *او* ثبت شده نصف مسیر عیب‌یابی است.
 */
import { API_BASE_URL } from '../config/constants';

type Level = 'fatal' | 'error' | 'warning';

interface ReportedEvent {
  level: Level;
  type: string;
  message: string;
  stack?: string;
  url?: string;
  route?: string;
  culprit?: string;
  context?: Record<string, unknown>;
}

const MAX_PER_SESSION = 40;
const FLUSH_DELAY = 2000;
const MAX_BATCH = 20;

/** الگوهایی که باگ ما نیستند و فقط لیست خطاها را کور می‌کنند. */
const IGNORED = [
  /ResizeObserver loop/i,
  /Non-Error promise rejection captured/i,
  /^Script error\.?$/i,
  /chrome-extension:|moz-extension:|safari-extension:/i,
  /Failed to fetch|NetworkError|Load failed/i,
  /AbortError|The operation was aborted|cancelled|canceled/i,
  // درخواست‌هایی که خودِ api.ts قبلاً با toast به کاربر نشان داده
  /Request failed with status code 40[13]/i,
];

const shouldIgnore = (message: string, stack?: string): boolean => {
  const haystack = `${message}\n${stack || ''}`;
  return IGNORED.some((rx) => rx.test(haystack));
};

let queue: ReportedEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let sentCount = 0;
let installed = false;
const seen = new Set<string>();

const localFingerprint = (e: ReportedEvent): string =>
  `${e.type}|${e.message.slice(0, 200)}|${(e.stack || '').split('\n')[1] || ''}`;

const endpoint = (): string => `${API_BASE_URL.replace(/\/$/, '')}/client-logs`;

const flush = (useBeacon = false): void => {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (queue.length === 0) return;

  const events = queue.splice(0, MAX_BATCH);
  const payload = JSON.stringify({ source: 'admin', events });

  try {
    if (useBeacon && navigator.sendBeacon) {
      // sendBeacon هدر دلخواه نمی‌پذیرد، پس این مسیر بدون توکن می‌رود.
      // خطا همچنان ثبت می‌شود، فقط بدون شناسهٔ کاربر — که بهتر از گم‌شدنش است.
      navigator.sendBeacon(endpoint(), new Blob([payload], { type: 'application/json' }));
      return;
    }

    const token = localStorage.getItem('accessToken');
    void fetch(endpoint(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: payload,
      keepalive: true,
    }).catch(() => {
      /* گزارش خطا حق ندارد خودش خطا بدهد */
    });
  } catch {
    /* noop */
  }
};

const schedule = (): void => {
  if (flushTimer) return;
  flushTimer = setTimeout(() => flush(false), FLUSH_DELAY);
};

/** گزارش دستی — از ErrorBoundary یا هر جای دیگر. */
export const reportError = (error: unknown, extra: Partial<ReportedEvent> = {}): void => {
  if (sentCount >= MAX_PER_SESSION) return;

  const err = error as { name?: string; message?: string; stack?: string };
  const message = String(err?.message || error || '').slice(0, 1000);
  if (!message) return;

  const stack = typeof err?.stack === 'string' ? err.stack.slice(0, 8000) : undefined;
  if (shouldIgnore(message, stack)) return;

  const event: ReportedEvent = {
    level: 'error',
    type: err?.name || 'Error',
    message,
    stack,
    url: window.location.href.slice(0, 500),
    route: window.location.pathname.slice(0, 200),
    ...extra,
    context: {
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      ...(extra.context || {}),
    },
  };

  const fp = localFingerprint(event);
  if (seen.has(fp)) return;
  seen.add(fp);

  sentCount += 1;
  queue.push(event);
  schedule();
};

/** نصب شنونده‌های سراسری. در `index.tsx` قبل از رندر صدا زده می‌شود. */
export const installErrorReporter = (): void => {
  if (installed) return;
  installed = true;

  window.addEventListener('error', (event) => {
    if (event.error) {
      reportError(event.error, { culprit: event.filename ? `${event.filename}:${event.lineno}` : undefined });
    } else if (event.message) {
      reportError(new Error(event.message), {
        culprit: event.filename ? `${event.filename}:${event.lineno}` : undefined,
      });
    }
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    reportError(reason instanceof Error ? reason : new Error(String(reason)), {
      type: reason instanceof Error ? reason.name : 'UnhandledRejection',
    });
  });

  window.addEventListener('pagehide', () => flush(true));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush(true);
  });
};

const errorReporter = { reportError, installErrorReporter };
export default errorReporter;
